/**
 * Utility functions for muscle-related operations
 * Extracted from TargetMuscle component for better modularity and reusability
 */

/**
 * Get bilateral muscles for automatic pairing based on compressed SVG IDs
 * @param {Array} muscles - Array of muscle IDs
 * @returns {Array} - Array of muscles including bilateral pairs
 */
export const getBilateralMuscles = (muscles) => {
  // Early return if no muscles provided
  if (!muscles || muscles.length === 0) {
    return [];
  }
  
  const bilateralMap = {
    // Most muscles in the compressed SVG are group elements, so no bilateral mapping needed
    // The SVG uses group IDs like 'chest', 'Biceps', 'Triceps', etc. that represent both sides
  };

  const expandedMuscles = new Set(muscles);
  
  muscles.forEach(muscle => {
    const bilateral = bilateralMap[muscle];
    if (bilateral) {
      expandedMuscles.add(bilateral);
    }
  });

  return Array.from(expandedMuscles);
};

/**
 * Format muscle name by replacing underscores with spaces and capitalizing words
 * @param {string} id - Muscle ID to format
 * @returns {string} - Formatted muscle name
 */
export const formatMuscleName = (id) => {
  return id.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
};

/**
 * Generate dynamic CSS for highlighting muscles
 * @param {Object} highlightedMuscles - Object with primary and secondary muscle arrays
 * @returns {string} - CSS string for muscle highlighting
 */
export const generateHighlightCSS = (highlightedMuscles) => {
  let css = '';
  
  // Primary muscles (indigo)
  highlightedMuscles.primary.forEach(muscleId => {
    const cssRule = `#${muscleId} { fill: #6366f1 !important; stroke: #4f46e5 !important; stroke-width: 3px !important; filter: drop-shadow(0 0 6px rgba(99, 102, 241, 0.4)) !important; }`;
    css += cssRule + ' ';
  });
  
  // Secondary muscles (amber)
  highlightedMuscles.secondary.forEach(muscleId => {
    const cssRule = `#${muscleId} { fill: #f59e0b !important; stroke: #d97706 !important; stroke-width: 2px !important; filter: drop-shadow(0 0 4px rgba(245, 158, 11, 0.3)) !important; }`;
    css += cssRule + ' ';
  });
  
  return css;
};